import { ArrowLeft, Bell, Building2, Handshake, MessageSquare } from 'lucide-react'
import { Helmet } from 'react-helmet'
import { Link, useParams, useSearchParams } from 'react-router'

import { Button } from '@/components/ui/button'
import { SkeletonRows } from '@/components/ui/skeleton'
import { ToggleTabs } from '@/components/ui/ToggleTabs'
import { StaffUserTab, useStaffUser } from '@/models/staffUser'
import { staffUserName } from '@/models/user'
import { CountLabel } from './CountLabel'
import { StaffActivityTab } from './StaffActivityTab'
import { StaffCustomersTab } from './StaffCustomersTab'
import { StaffLeadsTab } from './StaffLeadsTab'
import { StaffRemindersTab } from './StaffRemindersTab'
import { StaffUserHeader } from './StaffUserHeader'

function isTab(value: string): value is StaffUserTab {
  return (Object.values(StaffUserTab) as string[]).includes(value)
}

export function StaffUserDetail() {
  const { id = '' } = useParams<{ id: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const rawTab = searchParams.get('tab') ?? ''
  const tab = isTab(rawTab) ? rawTab : StaffUserTab.ACTIVITY
  const { staffUser, isPending, isError } = useStaffUser(id)

  // Filters and page belong to the tab they were set on, so a new tab starts clean.
  function onTabChange(value: string) {
    setSearchParams(value === StaffUserTab.ACTIVITY ? {} : { tab: value }, { preventScrollReset: true })
  }

  return (
    <>
      <Helmet>
        <title>{staffUser ? staffUserName(staffUser) : 'Staff user'}</title>
      </Helmet>
      <div className="flex max-w-5xl flex-col gap-5">
        <div>
          <Button variant="link" asChild className="h-auto p-0">
            <Link to="/insure/internal/staff-users">
              <ArrowLeft className="size-4" />
              Staff users
            </Link>
          </Button>
        </div>
        {isPending && <SkeletonRows rows={2} />}
        {isError && <p className="text-destructive">Couldn’t load this staff user.</p>}
        {staffUser && (
          <>
            <StaffUserHeader staffUser={staffUser} />
            <ToggleTabs
              value={tab}
              onValueChange={onTabChange}
              items={[
                {
                  value: StaffUserTab.ACTIVITY,
                  icon: <MessageSquare />,
                  label: <CountLabel label="Activity" count={staffUser.activityCounts.all} />,
                },
                {
                  value: StaffUserTab.CUSTOMERS,
                  icon: <Building2 />,
                  label: <CountLabel label="Customers" count={staffUser.counts.customers} />,
                },
                {
                  value: StaffUserTab.LEADS,
                  icon: <Handshake />,
                  label: <CountLabel label="Sales leads" count={staffUser.counts.salesLeads} />,
                },
                {
                  value: StaffUserTab.REMINDERS,
                  icon: <Bell />,
                  label: <CountLabel label="Reminders" count={staffUser.counts.reminders} />,
                },
              ]}
            >
              {tab === StaffUserTab.ACTIVITY && <StaffActivityTab staffUser={staffUser} />}
              {tab === StaffUserTab.CUSTOMERS && <StaffCustomersTab staffUserId={staffUser.id} />}
              {tab === StaffUserTab.LEADS && <StaffLeadsTab staffUserId={staffUser.id} />}
              {tab === StaffUserTab.REMINDERS && <StaffRemindersTab staffUserId={staffUser.id} />}
            </ToggleTabs>
          </>
        )}
      </div>
    </>
  )
}
